import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'ucat_token';
const USER_CACHE_KEY = 'ucat_user_cache';

function readUserCache(token) {
  if (!token) return null;
  try {
    const parsed = JSON.parse(sessionStorage.getItem(USER_CACHE_KEY) || 'null');
    if (!parsed?.user || parsed.token !== token) return null;
    return parsed.user;
  } catch {
    return null;
  }
}

function writeUserCache(token, user) {
  try {
    if (!token || !user) sessionStorage.removeItem(USER_CACHE_KEY);
    else sessionStorage.setItem(USER_CACHE_KEY, JSON.stringify({ token, user }));
  } catch {
    /* private mode / quota */
  }
}

function subscriptionActive(user) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  const tier = user.subscription_tier?.toLowerCase();
  if (!tier || tier === 'none' || tier === 'free') return false;
  return true;
}

function tokenExpired(token) {
  if (!token) return true;
  try {
    const part = token.split('.')[1];
    if (!part) return true;
    const padded = part.replace(/-/g, '+').replace(/_/g, '/');
    const b64 = padded + '='.repeat((4 - (padded.length % 4)) % 4);
    const payload = JSON.parse(atob(b64));
    return typeof payload.exp === 'number' && payload.exp * 1000 <= Date.now() + 2000;
  } catch {
    // Invalid client parse must not wipe a live session. The API decides.
    return false;
  }
}

function readStoredToken() {
  const existing = localStorage.getItem(TOKEN_KEY);
  if (!existing || tokenExpired(existing)) {
    localStorage.removeItem(TOKEN_KEY);
    writeUserCache(null, null);
    return null;
  }
  return existing;
}

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => readStoredToken());
  const [user, setUser] = useState(() => readUserCache(readStoredToken()));
  const [loading, setLoading] = useState(() => {
    const existing = readStoredToken();
    return Boolean(existing && !readUserCache(existing));
  });

  const api = useRef(null);
  if (!api.current) {
    const existing = readStoredToken();
    api.current = axios.create({
      baseURL: `${API_URL}/api`,
      headers: existing ? { Authorization: `Bearer ${existing}` } : {},
    });
  }
  const client = api.current;

  useEffect(() => {
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
    else delete client.defaults.headers.Authorization;
  }, [token, client]);

  const persistSession = useCallback((accessToken, userData) => {
    if (accessToken) {
      localStorage.setItem(TOKEN_KEY, accessToken);
      client.defaults.headers.Authorization = `Bearer ${accessToken}`;
    } else {
      localStorage.removeItem(TOKEN_KEY);
      delete client.defaults.headers.Authorization;
    }
    writeUserCache(accessToken, userData);
    setToken(accessToken);
    setUser(userData);
  }, [client]);

  const fetchUser = useCallback(async () => {
    if (!token) {
      setUser(null);
      setLoading(false);
      writeUserCache(null, null);
      return;
    }
    try {
      const response = await client.get('/users/me');
      setUser(response.data);
      writeUserCache(token, response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        writeUserCache(null, null);
        setToken(null);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, [token, client]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    fetchUser();
  }, [fetchUser, token]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    persistSession(access_token, userData);
    setLoading(false);
    return userData;
  }, [persistSession]);

  const register = useCallback(async (email, password, name) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, { email, password, name });
    const { access_token, user: userData } = response.data;
    persistSession(access_token, userData);
    setLoading(false);
    return userData;
  }, [persistSession]);

  const logout = useCallback(() => {
    persistSession(null, null);
    setLoading(false);
  }, [persistSession]);

  const updateSettings = useCallback(async (settings) => {
    const response = await client.put('/users/me', settings);
    setUser(response.data);
    writeUserCache(token, response.data);
    return response.data;
  }, [client, token]);

  const value = useMemo(() => ({
    user,
    token,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    hasActiveSubscription: subscriptionActive(user),
    needsOnboarding: !!user && user.onboarding_completed === false,
    login,
    register,
    logout,
    updateSettings,
    refreshUser: fetchUser,
    api: client,
  }), [user, token, loading, login, register, logout, updateSettings, fetchUser, client]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
